import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import React from "react";
import { Button } from "../components/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { exportHierarchyToExcel } from "./ExportUtils";
import { Group, OrgChartNode } from "./GroupStore";

const getTreeItemId = (node: OrgChartNode, path: string) => `${path}:${node.id}`;

// MUI item IDs identify occurrences, not users. A user can legitimately occur in
// several branches, so include the traversal path to keep every item ID unique.
const renderTree = (node: OrgChartNode, path: string, ancestorIds = new Set<string>()) => {
    const itemId = getTreeItemId(node, path);
    const isCycle = ancestorIds.has(node.id);
    const nextAncestorIds = new Set(ancestorIds);
    nextAncestorIds.add(node.id);

    return (
        <TreeItem key={itemId} itemId={itemId} label={node.name}>
            {!isCycle &&
                Array.isArray(node.children) &&
                node.children.map((child, index) => renderTree(child, `${path}.${index}`, nextAncestorIds))}
        </TreeItem>
    );
};

/**
 * Recursively collects all node IDs from a group (or array of groups).
 *
 * @param {Object|Array} groups - A group object or an array of group objects.
 *                                Each group is expected to have an "id" property,
 *                                and optionally a "children" array property.
 * @returns {Array} An array of node IDs (strings or numbers).
 */
export function getAllGroupIds(groups: OrgChartNode | OrgChartNode[]) {
    const ids = [] as string[];

    // Recursive helper function to traverse each node.
    function traverse(node: OrgChartNode, path: string, ancestorIds = new Set<string>()) {
        if (!node) return;

        ids.push(getTreeItemId(node, path));

        // Do not recurse through a malformed membership cycle. Repeated users in
        // separate branches are still traversed because ancestry is branch-local.
        if (ancestorIds.has(node.id)) return;

        const nextAncestorIds = new Set(ancestorIds);
        nextAncestorIds.add(node.id);

        // If the node has children, traverse each child.
        if (node.children && Array.isArray(node.children)) {
            node.children.forEach((child, index) => traverse(child, `${path}.${index}`, nextAncestorIds));
        }
    }

    // Check if groups is an array; if not, assume it's a single object.
    if (Array.isArray(groups)) {
        groups.forEach((group, index) => traverse(group, `${index}`));
    } else {
        traverse(groups, "0");
    }

    return ids;
}

interface OrgChartGridProps {
    orgChart: OrgChartNode[];
    groups: Group[];
}

const OrgChartGrid: React.FC<OrgChartGridProps> = ({ orgChart, groups }) => {
    const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

    const treeData = Array.isArray(orgChart) ? orgChart : [orgChart];

    const handleExpandedItemsChange = (event: React.SyntheticEvent, itemIds: string[]) => {
        setExpandedItems(itemIds);
    };

    const handleExpandClick = () => {
        setExpandedItems((oldExpanded) => (oldExpanded.length === 0 ? getAllGroupIds(treeData) : []));
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <div className="space-y-4">
                {/* Controls Section */}
                <div className="flex gap-2 pb-4 border-b border-gray-200">
                    <Button
                        variant="outline-primary"
                        onClick={() => {
                            exportHierarchyToExcel(orgChart);
                        }}
                    >
                        <FontAwesomeIcon icon={["fad", "download"]} className="mr-2" />
                        Export
                    </Button>
                    <Button variant="outline-secondary" onClick={handleExpandClick}>
                        <FontAwesomeIcon icon={["fad", expandedItems.length === 0 ? "expand-arrows-alt" : "compress-arrows-alt"]} className="mr-2" />
                        {expandedItems.length === 0 ? "Expand all" : "Collapse all"}
                    </Button>
                </div>
                
                {/* Tree View Section */}
                <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                    <SimpleTreeView expandedItems={expandedItems} onExpandedItemsChange={handleExpandedItemsChange}>
                        {treeData.map((node, index) => renderTree(node, `${index}`))}
                    </SimpleTreeView>
                </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 min-h-64 flex items-center justify-center text-gray-500">
                {/* This space could be used for additional information or controls */}
                <div className="text-center">
                    <FontAwesomeIcon icon={["fad", "sitemap"]} className="text-4xl mb-2 text-gray-400" />
                    <p className="text-sm">Organization chart visualization</p>
                </div>
            </div>
        </div>
    );
};

export default OrgChartGrid;
